/**
 * The renewal quote (tracker item 85): what next season's visits would be,
 * priced at each visit's renewal price (never the chart), with a start date
 * and "Create renewal". Creating one schedules and bills nothing; it waits
 * for staff approval.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IBundlesApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { enrollmentPath } from './bundleUi';

export const PRICE_SOURCE_TEXT: Record<types.RenewalPriceSource, string> = {
  RENEWAL_PRICE: 'Renewal price',
  LAST_CHARGED: 'Price paid today',
  ORIGINAL: 'Original price',
};

export function QuoteTable({ quote }: { quote: types.RenewalQuote }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm" aria-label="Renewal quote">
        <thead className="bg-gray-50">
          <tr>{['Visit', 'Item', 'Date', 'Price', 'Based on', 'Total'].map((h) => <th key={h} scope="col" className="px-3 py-2 text-left font-medium text-gray-600">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {quote.steps.map((s) => (
            <tr key={s.step}>
              <td className="px-3 py-2">{s.step}</td>
              <td className="px-3 py-2 break-words">{s.itemName}{Number(s.quantity) !== 1 && <span className="text-gray-600"> × {s.quantity}</span>}</td>
              <td className="px-3 py-2 whitespace-nowrap">{formatJobDate(s.plannedDate)}</td>
              <td className="px-3 py-2">{formatJobMoney(s.unitPrice)}</td>
              <td className="px-3 py-2 text-gray-600">{PRICE_SOURCE_TEXT[s.priceSource]}{s.currentUnitPrice && s.priceSource === 'RENEWAL_PRICE' && Number(s.currentUnitPrice) !== Number(s.unitPrice) ? ` (today ${formatJobMoney(s.currentUnitPrice)})` : ''}</td>
              <td className="px-3 py-2">{formatJobMoney(s.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={5} className="px-3 py-1 text-right text-gray-600">Subtotal</td><td className="px-3 py-1">{formatJobMoney(quote.subtotal)}</td></tr>
          <tr><td colSpan={5} className="px-3 py-1 text-right text-gray-600">Estimated tax</td><td className="px-3 py-1">{formatJobMoney(quote.estimatedTax)}</td></tr>
          <tr className="font-semibold"><td colSpan={5} className="px-3 py-2 text-right">Estimated total</td><td className="px-3 py-2" data-testid="quote-total">{formatJobMoney(quote.estimatedTotal)}</td></tr>
        </tfoot>
      </table>
    </div>
  );
}

interface DialogProps {
  enrollment: Pick<types.BundleEnrollment, 'id' | 'bundleName'>;
  onClose: () => void;
  /** The pending renewal that was created (or already existed) */
  onCreated: (result: types.RenewalCreateResult) => void;
}

export function RenewalQuoteDialog({ enrollment, onClose, onCreated }: DialogProps) {
  const api = useMemo(() => createAPIClient() as IBundlesApi, []);
  const [startDate, setStartDate] = useState<string>('');
  const [quote, setQuote] = useState<types.RenewalQuote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); origin?.focus(); };
  }, [onClose]);

  useEffect(() => {
    let active = true;
    setError(null);
    api.getEnrollmentQuote(enrollment.id, startDate || undefined)
      .then((result) => { if (!active) return; setQuote(result); if (!startDate) setStartDate(result.startDate); })
      .catch((err) => { if (active) setError(getErrorMessage(err, 'Could not load the renewal quote')); });
    return () => { active = false; };
  }, [api, enrollment.id, startDate]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      onCreated(await api.createRenewal(enrollment.id, startDate || undefined));
    } catch (err) {
      setError(getErrorMessage(err, 'Could not create the renewal'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="renew-title" className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl focus:outline-none sm:max-w-3xl sm:rounded-lg">
        <h2 id="renew-title" className="text-lg font-semibold text-gray-900">Renew {enrollment.bundleName}</h2>
        <p className="mt-1 text-sm text-gray-600">Next season&apos;s quote at each visit&apos;s renewal price. Creating the renewal schedules and bills nothing; it waits for your approval.</p>
        {error && <Toast kind="error">{error}</Toast>}
        <div className="mt-4 max-w-xs">
          <label htmlFor="renew-start" className={labelClass}>Start date</label>
          <input id="renew-start" type="date" className={inputClass} value={startDate} disabled={busy} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="mt-4">
          {!quote && !error && <p role="status" className="text-sm text-gray-500">Loading the quote...</p>}
          {quote && (
            <>
              {quote.renewal && (
                <p role="note" className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  A renewal already exists ({quote.renewal.status === 'PENDING_APPROVAL' ? 'pending approval' : quote.renewal.status.toLowerCase()}). <Link to={enrollmentPath(quote.renewal.id)} className="font-semibold underline">Open it</Link>
                </p>
              )}
              {quote.skipped.length > 0 && <p role="note" className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Not renewed: {quote.skipped.map((s) => s.itemName).join(', ')}.</p>}
              <QuoteTable quote={quote} />
            </>
          )}
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={secondaryButtonClass} onClick={onClose} disabled={busy}>Close</button>
          <button type="button" className={primaryButtonClass} disabled={!quote || busy || !startDate} onClick={create}>{busy ? 'Creating...' : quote?.renewal ? 'Open existing renewal' : 'Create renewal'}</button>
        </div>
      </div>
    </div>
  );
}
